import { useEffect, useState, type CSSProperties } from "react"
import { Link } from "react-router-dom"
import api from "@/lib/api"
import { Button } from "@/components/ui/button"
import { PageEmpty } from "@/components/page"

interface EnrolledCourse {
  id: string
  title: string
  slug: string
  enrollment_status: string
  total_lessons: number
  completed_lessons: number
  last_lesson?: { title: string } | null
}

type LoadState = "loading" | "ready" | "error"

const lessonToSlug = (title: string) => title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")

// Qué le falta a un curso para emitir certificado. Solo se conocen las lecciones: la API
// no expone cuántos exámenes obligatorios quedan, así que con todas las lecciones hechas
// el bloqueo se nombra por el examen.
function blocker(c: EnrolledCourse) {
  const left = c.total_lessons - c.completed_lessons
  if (c.total_lessons > 0 && left <= 0) return "Solo falta aprobar los exámenes obligatorios"
  if (c.completed_lessons === 0) return "Aún no empiezas"
  return left === 1 ? "Falta 1 lección" : `Faltan ${left} lecciones`
}

const pct = (c: EnrolledCourse) => (c.total_lessons > 0 ? Math.round((c.completed_lessons / c.total_lessons) * 100) : 0)

// Estado vacío de /certificados: en vez de "no hay nada", la ruta hacia el primer certificado
// con los cursos del estudiante ordenados de más a menos avanzado.
export function CertificateRoadmap() {
  const [state, setState] = useState<LoadState>("loading")
  const [courses, setCourses] = useState<EnrolledCourse[]>([])
  const [seedbedName, setSeedbedName] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    Promise.allSettled([api.get("/academy/courses/enrolled/me"), api.get("/academy/seedbeds/me")]).then(([enrolled, seedbed]) => {
      if (cancelled) return
      if (seedbed.status === "fulfilled") setSeedbedName(seedbed.value.data?.data?.seedbeds?.[0]?.seedbedName ?? null)
      if (enrolled.status === "fulfilled") {
        const list = (enrolled.value.data?.data ?? []) as EnrolledCourse[]
        setCourses(list.filter((c) => c.enrollment_status !== "completed").sort((a, b) => pct(b) - pct(a)).slice(0, 6))
        setState("ready")
      } else {
        setState("error")
      }
    })
    return () => { cancelled = true }
  }, [])

  const intro = seedbedName
    ? `Tu semillero (${seedbedName}) te habilitó cursos. Un certificado se emite al terminar uno y aprobar sus exámenes obligatorios.`
    : "Un certificado se emite al terminar un curso y aprobar sus exámenes obligatorios. Trae un código que cualquiera puede comprobar."

  // Si el progreso no carga o no hay cursos, el estado vacío sigue sirviendo solo con texto y un enlace.
  if (state !== "ready" || courses.length === 0) {
    return (
      <PageEmpty
        variant="ruled"
        title={seedbedName ? "Tu primer certificado empieza en un curso de tu semillero" : "Tu primer certificado empieza en un curso"}
        description={intro}
        action={
          <Button render={<Link to="/dashboard" />} nativeButton={false} size="lg" className="min-h-11 px-5 font-bold">
            {seedbedName ? "Ver mis cursos disponibles" : "Explorar cursos"}
          </Button>
        }
      />
    )
  }

  return (
    <section aria-labelledby="ruta-certificado" className="w-full space-y-4">
      <div className="space-y-1">
        <h2 id="ruta-certificado" className="text-lg font-semibold tracking-tight text-foreground">
          Estás en camino a tu primer certificado
        </h2>
        <p className="max-w-prose text-sm text-muted-foreground">{intro}</p>
      </div>
      <ul className="grid gap-x-10 border-t border-border lg:grid-cols-2 2xl:grid-cols-3">
        {courses.map((course, i) => {
          const prog = pct(course)
          const href = course.last_lesson ? `/courses/${course.slug}/${lessonToSlug(course.last_lesson.title)}` : `/courses/${course.slug}`
          return (
            <li
              key={course.id}
              className="motion-stagger-item flex flex-col gap-3 border-b border-border py-4 sm:flex-row sm:items-center sm:gap-6"
              style={{ "--i": i } as CSSProperties}
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-base font-medium text-foreground">{course.title}</p>
                <div
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={prog}
                  aria-label={`Progreso del curso ${course.title}`}
                  className="mt-2 h-1 w-full overflow-hidden rounded-full bg-muted"
                >
                  <div className="motion-progress motion-progress-in h-full w-full bg-primary" style={{ transform: `scaleX(${prog / 100})` }} />
                </div>
                <p className="mt-2 text-sm tabular-nums text-muted-foreground">
                  <span className="font-semibold text-foreground">{prog}%</span> · {blocker(course)}
                </p>
              </div>
              <Button
                render={<Link to={href} aria-label={`${course.completed_lessons === 0 ? "Empezar" : "Continuar"} ${course.title}`} />}
                nativeButton={false}
                variant={i === 0 ? "default" : "outline"}
                size="lg"
                className="min-h-11 shrink-0 px-5 font-semibold"
              >
                {course.completed_lessons === 0 ? "Empezar" : "Continuar"}
              </Button>
            </li>
          )
        })}
      </ul>
    </section>
  )
}
